import React from 'react';
import { Checkbox, Tooltip } from 'antd';
import { useAtom, useAtomValue } from 'jotai';
import { useSelector } from 'react-redux';
import { tempKPIAtom, categoryMetaKeysAtom, configAtom, allSelectedPresentAtom } from '../atoms';
import { HourglassOutlined, WarningOutlined } from '@ant-design/icons';
import { HEAVY_METRICS_MESSAGE, isAppModule } from '@utils/constants';
import { kpiDropdownFeatureSelector } from '@store/common/selectors';
import {
  CheckBoxList,
  StyledCheckBox,
  MetricDivider,
  StyledText,
  CheckboxWrapper,
  CategoryWrapper,
  StyledBetaMetric,
  PercentileIcon,
} from '../styled';
import KPIDropdown from './KPIDropdown';
import { betaMetricList, deprecationMetricList, deprecatingSoon } from '../constants';
import presetStore from '@src/globalStore/presetsStore';
import PresetShortcut from '../../PresetShortcut';
import { useCaseKeyAtom } from '@src/atoms/pageMeta';
import { getPercentileMetrics } from '@src/utils/metrics/metricHelpers';

const CheckboxGroup = ({
  categoryMeta,
  categoryKey,
  inputList = [],
  onChange,
  onBulkChange,
  setRef,
  groupChecked = false,
  hasSearchText = false,
  emptyText,
  isFlow = false,
}) => {
  const productCategoryMetaKeys: any = useAtomValue(categoryMetaKeysAtom);
  const allSelectedPresent = useAtomValue(allSelectedPresentAtom);
  const metricConfig = useAtomValue(configAtom);
  const listItemKeys = inputList.map((item: any) => item.key);
  const KPIDropdownEnabled = useSelector(kpiDropdownFeatureSelector);
  const [tempKpi, setTempKpi] = useAtom(tempKPIAtom);
  const isAppContext = isAppModule;
  const useCaseKey = useAtomValue(useCaseKeyAtom);

  const { presets = {} } = presetStore((state) => state);
  const presetsForCurrentMetricCategory = (Object.values(presets) || []).filter(
    (preset: any) => preset.linkedMetricCategory === categoryKey,
  );

  const handleClick = (event) => {
    const checked = event.target.checked;
    const result = inputList.filter((item: any) => !item.disabled).map((item: any) => item.key);
    onBulkChange(checked, result);
  };

  const emptyList = listItemKeys.length === 0;
  const showKPIDropdown = isAppContext ? true : KPIDropdownEnabled;

  const getCheckboxGroup = () => {
    if (emptyList) {
      return emptyText ?? <>No data</>;
    }
    return (
      <CheckboxWrapper count={listItemKeys.length}>
        {inputList.map((c: any) => {
          return (
            <StyledCheckBox key={c.key}>
              <Checkbox
                onChange={onChange}
                checked={c.checked}
                disabled={c.disabled}
                value={c.key}
                key={c.key}
              >
                {c.name}
                {betaMetricList.includes(c.key) ? (
                  <StyledBetaMetric>Beta</StyledBetaMetric>
                ) : null}
                {deprecationMetricList.includes(c.key) ? (
                  <Tooltip title="Scheduled for Deprecation">
                    <WarningOutlined style={{ color: '#ffa500', marginLeft: 4 }} />
                  </Tooltip>
                ) : null}
                {deprecatingSoon.includes(c.key) ? (
                  <Tooltip title="Deprecating Soon">
                    <WarningOutlined style={{ color: '#ffa500', marginLeft: 4 }} />
                  </Tooltip>
                ) : null}
                {c.isHeavyMetric && c.checked ? (
                  <Tooltip title={HEAVY_METRICS_MESSAGE}>
                    <HourglassOutlined />
                  </Tooltip>
                ) : null}
                {isAppModule &&
                getPercentileMetrics().includes(c.key) &&
                metricConfig.showPercentile ? (
                  <Tooltip title="Percentile Supported">
                    <PercentileIcon>P</PercentileIcon>
                  </Tooltip>
                ) : null}
              </Checkbox>
            </StyledCheckBox>
          );
        })}
      </CheckboxWrapper>
    );
  };

  let disableGrouped = false;
  if (!isAppContext && allSelectedPresent) {
    if (groupChecked) {
      disableGrouped = false;
    } else {
      disableGrouped = true;
    }
  }

  return (
    <CheckBoxList key={categoryKey} ref={setRef}>
      <CategoryWrapper>
        <StyledText>
          <Checkbox
            onChange={handleClick}
            key={categoryKey}
            checked={groupChecked}
            disabled={emptyList || hasSearchText || disableGrouped}
            style={{ fontSize: 16 }}
          >
            {categoryMeta.displayName}
          </Checkbox>
        </StyledText>

        {isFlow && isAppModule ? (
          <PresetShortcut
            shortcutType="flow-preset"
            categoryKey={categoryKey}
            linkFrom="metrics"
            key={categoryKey}
          />
        ) : null}

        {/* show preset links if current dashboard is not a preset itself */}
        {!useCaseKey &&
          presetsForCurrentMetricCategory.map((preset: any) => {
            return (
              <PresetShortcut
                shortcutType="preset"
                preset={preset}
                linkFrom="metrics"
                key={preset.key}
              />
            );
          })}
      </CategoryWrapper>
      <MetricDivider />
      {[productCategoryMetaKeys.VIDEO_SPI, productCategoryMetaKeys.ECO_QOE].includes(categoryKey) &&
        showKPIDropdown &&
        !!tempKpi &&
        !!setTempKpi && <KPIDropdown tempKpi={tempKpi} setTempKpi={setTempKpi} />}
      {getCheckboxGroup()}
    </CheckBoxList>
  );
};

export default CheckboxGroup;
